import styles from './LegalCaseSummaryCards.module.scss';
import { RISK_COLORS, RISK_LABEL_CONFIG } from '../../../../RiskIndicators/RiskIndicatorConstants';
import { useAnimatedCounter } from '../../../../../common/hooks/useAnimatedCounter';
import type { LegalCasesSummary, LegalCasesSummaryBucket } from './legalCasesType';

type BoxKey = keyof LegalCasesSummary;

const BOXES: { key: BoxKey; title: string; tone: { color: string; backgroundColor: string } }[] = [
    { key: 'confirmedAgainstCompany', title: 'Confirmed Cases Against Company', tone: RISK_COLORS.CONFIRMED },
    { key: 'confirmedAgainstDirectors', title: 'Confirmed Cases Against Directors', tone: RISK_COLORS.CONFIRMED },
    { key: 'probableAgainstCompanyOrDirectors', title: 'Probable Against Company/Directors', tone: RISK_COLORS.PROBABLE },
    { key: 'filedByCompanyOrDirectors', title: 'Cases Filed by Company or Directors', tone: RISK_COLORS.CONFIRMED },
];

// Zero-open always renders the literal "No Open" in green, regardless of the box's own tone —
// a red/amber pill reading "0 Open" would read as an active finding when there is none.
const OpenCountPill = ({ openCases, tone }: { openCases: number; tone: { color: string; backgroundColor: string } }) => {
    const isZero = openCases === 0;
    const style = isZero ? RISK_LABEL_CONFIG.NO_RISK : tone;
    return (
        <span className={styles.OpenPill} style={{ color: style.color, backgroundColor: style.backgroundColor }}>
            {isZero ? 'No Open' : `${openCases} Open`}
        </span>
    );
};

// Extracted so useAnimatedCounter — a hook — is called from a real component instance per box,
// not from inside the BOXES.map callback (which the rules-of-hooks lint forbids).
// The count animates on mount (animateOnMount) because these cards only mount once the data has
// already arrived (behind LegalCaseSummaryCards.Skeleton) — the default snap-to-target behaviour
// would never visibly animate here.
const CaseSummaryCard = ({
    title,
    tone,
    bucket,
    directorsWithCases,
}: {
    title: string;
    tone: { color: string; backgroundColor: string };
    bucket: LegalCasesSummaryBucket;
    directorsWithCases: number | null;
}) => {
    const animatedTotalCases = useAnimatedCounter(bucket.totalCases, { animateOnMount: true, decimals: 0 });
    return (
        <div className={styles.Card}>
            <div className={styles.CardTitle}>{title}</div>
            <div className={styles.CardBody}>
                <span className={styles.CardCount}>
                    {animatedTotalCases} Cases
                    {directorsWithCases != null && ` / ${directorsWithCases} Director`}
                </span>
                <OpenCountPill openCases={bucket.openCases} tone={tone} />
            </div>
        </div>
    );
};

// The frontend renders these four figures VERBATIM — no addition, no filtering, no bucketing.
// All arithmetic already happened in polus; recomputing here would risk disagreeing
// with the Legal Cases column, which reads the same backend numbers by construction.
function LegalCaseSummaryCards({ summary }: { summary: LegalCasesSummary }) {
    return (
        <div className={styles.SummaryCards}>
            {BOXES.map(({ key, title, tone }) => {
                const bucket: LegalCasesSummaryBucket = summary[key];
                const directorsWithCases =
                    key === 'confirmedAgainstDirectors' ? summary.confirmedAgainstDirectors.directorsWithCases : null;
                return <CaseSummaryCard key={key} title={title} tone={tone} bucket={bucket} directorsWithCases={directorsWithCases} />;
            })}
        </div>
    );
}

// Same four-box footprint, no numerals, no "Cases" word, no green "No Open" pill — rendered
// while the fetch is pending or the response is still PROCESSING, so nothing jumps when
// the real cards replace it.
LegalCaseSummaryCards.Skeleton = function LegalCaseSummaryCardsSkeleton() {
    return (
        <div className={styles.SummaryCards}>
            {BOXES.map(({ key }) => (
                <div className={styles.Card} key={key}>
                    <span className={styles.SkeletonTitleBar} />
                    <span className={styles.SkeletonBodyBar} />
                </div>
            ))}
        </div>
    );
};

export default LegalCaseSummaryCards;
